import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Images, MapPin, Clock, Palette, Heart, Share2, Tag,
  Wallet, Megaphone, LogOut, Disc3, Menu, X, RectangleHorizontal,
} from "lucide-react";

const links = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/app/collection", label: "Collection", icon: Images, testid: "nav-collection" },
  { to: "/app/map", label: "Gig Map", icon: MapPin, testid: "nav-map" },
  { to: "/app/timeline", label: "Timeline", icon: Clock, testid: "nav-timeline" },
  { to: "/app/artists", label: "Artists", icon: Palette, testid: "nav-artists" },
  { to: "/app/wishlist", label: "Grails", icon: Heart, testid: "nav-wishlist" },
  { to: "/app/categories", label: "Categories", icon: Tag, testid: "nav-categories" },
  { to: "/app/share", label: "Share", icon: Share2, testid: "nav-share" },
];

const adminLinks = [
  { to: "/app/wallet", label: "Wallet", icon: Wallet, testid: "nav-wallet" },
  { to: "/app/sponsors", label: "Sponsors", icon: Megaphone, testid: "nav-sponsors" },
  { to: "/app/ads", label: "Ad Manager", icon: RectangleHorizontal, testid: "nav-ads" },
];

const itemCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-wider whitespace-nowrap border-l-2 transition-colors ${
    isActive
      ? "bg-elevated text-acid border-acid"
      : "text-muted-gray border-transparent hover:text-paper hover:bg-elevated/60"
  }`;

export default function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // close the menu whenever the route changes
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login");
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-surface/85 backdrop-blur-md border-b border-stroke">
      <div className="flex items-center justify-between gap-3 px-4 lg:px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="menu-toggle"
            aria-label="Menu"
            aria-expanded={open}
            className={`flex items-center gap-2 px-3 py-2 border border-stroke font-mono text-xs uppercase tracking-wider text-paper hover:border-acid hover:text-acid transition-colors ${open ? "border-acid text-acid neon-glow" : ""}`}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
            <span className="hidden sm:inline">Menu</span>
          </button>
          <div className="flex items-center gap-2">
            <Disc3 className="text-acid" size={24} />
            <div className="leading-none">
              <div className="font-display text-2xl text-paper">POSTERVAULT</div>
              <div className="font-mono text-[9px] tracking-[0.3em] text-muted-gray hidden sm:block">FAV POSTER PORTFOLIOS</div>
            </div>
          </div>
        </div>
        <div className="min-w-0 text-right hidden sm:block" data-testid="nav-user">
          <div className="font-mono text-xs text-paper truncate">{user?.name}</div>
          <div className="font-mono text-[10px] text-muted-gray truncate">{user?.email}</div>
        </div>
      </div>

      {/* Dropdown anchored to the upper-left corner */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            data-testid="menu-backdrop"
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute left-4 top-[calc(100%-4px)] z-50 w-64 bg-surface border border-acid/40 hard-shadow animate-fade-up"
            data-testid="dropdown-menu"
          >
            <nav className="py-2">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testid} className={itemCls}>
                  <l.icon size={16} /> {l.label}
                </NavLink>
              ))}

              {user?.role === "admin" && (
                <>
                  <div className="px-4 pt-3 pb-1 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-gray/70">Admin</div>
                  {adminLinks.map((l) => (
                    <NavLink key={l.to} to={l.to} data-testid={l.testid} className={itemCls}>
                      <l.icon size={16} /> {l.label}
                    </NavLink>
                  ))}
                </>
              )}

              <div className="border-t border-stroke mt-2 pt-2 sm:hidden px-4 pb-1">
                <div className="font-mono text-xs text-paper truncate">{user?.name}</div>
                <div className="font-mono text-[10px] text-muted-gray truncate">{user?.email}</div>
              </div>

              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="w-full flex items-center gap-3 px-4 py-3 font-mono text-xs uppercase tracking-wider text-muted-gray border-l-2 border-transparent hover:text-magenta hover:bg-elevated/60 transition-colors"
              >
                <LogOut size={16} /> Log Out
              </button>
            </nav>
          </div>
        </>
      )}
    </header>
  );
}
